import { memo, useEffect, useState, useMemo } from 'react';
import gql from 'graphql-tag';

import WorkflowRow, {
  fragments as workflowRowFragments
} from './row/row.workflowList';

import { cn } from '@/shadcn/lib/utils';
import * as Sentry from '@sentry/react';
import { notify } from '@/components/toast';
import { z } from 'zod';

const favoriteWorkflowsSchema = z.array(z.string());

export const WorkflowsList = memo(
  ({ className = '', workflows = {}, isSuperuser = false, userId }) => {
    const { nodes: workflowsList = [] } = workflows;
    const [favWfIds, setFavWfIds] = useState();

    const favoriteStoreKey = `${userId}-favoriteWorkflows`;

    const onFavoriteChange = (rowId, value) => {
      const toggleRowIdInList = list => {
        const prevList = list ?? [];
        return value
          ? [...prevList, rowId]
          : prevList.filter(id => id !== rowId);
      };

      setFavWfIds(prev => toggleRowIdInList(prev));

      try {
        const favoriteWorkflows = getStoredFavoriteWorkflows();
        const updated = toggleRowIdInList(favoriteWorkflows);
        localStorage.setItem(favoriteStoreKey, JSON.stringify(updated));
      } catch (error) {
        console.error('Error updating favorite workflows:', error);
        Sentry.captureException(error, {
          tags: { component: 'WorkflowsList' },
          extra: { rowId, value }
        });
        notify.error(
          'An error occurred while updating favorite workflows. Please try again.'
        );
      }
    };

    const getStoredFavoriteWorkflows = () => {
      try {
        const rawData = JSON.parse(
          localStorage.getItem(favoriteStoreKey) || '[]'
        );
        return favoriteWorkflowsSchema.parse(rawData);
      } catch (error) {
        console.error(
          'Error parsing favorite workflows from localStorage:',
          error
        );
        Sentry.captureException(error, {
          tags: { component: 'WorkflowsList' },
          extra: { favoriteStoreKey }
        });
        return [];
      }
    };

    const getSortedWorkflows = () => {
      const favWorkflowIds = favWfIds || getStoredFavoriteWorkflows();
      if (!favWfIds) {
        setFavWfIds(favWorkflowIds);
      }
      const favoriteWorkflows = workflowsList.filter(workflow =>
        favWorkflowIds.includes(workflow.rowId)
      );
      const nonFavoriteWorkflows = workflowsList.filter(
        workflow => !favWorkflowIds.includes(workflow.rowId)
      );
      return [...favoriteWorkflows, ...nonFavoriteWorkflows];
    };

    const sortedWorkflows = useMemo(getSortedWorkflows, [
      favWfIds,
      workflowsList
    ]);

    return (
      <div
        className={cn(
          className,
          'bg-card flex shrink-0 flex-col flex-nowrap overflow-visible px-4 py-8'
        )}
      >
        <div className="mb-4 flex flex-row justify-between px-4">
          <div className="mr-8 flex shrink-0 flex-col flex-nowrap text-justify text-base font-semibold">
            Your workflows
            <div className="bg-secondary h-1 w-4 rounded-lg" />
          </div>
        </div>
        <div className="scrollbar-thin relative max-h-[238px] overflow-y-auto">
          {sortedWorkflows.length > 0 ? (
            <table className="w-full border-spacing-0">
              <thead className="bg-card text-muted-foreground sticky top-0 z-1 text-xs font-medium tracking-wider whitespace-nowrap">
                <tr className="bg-card [&>th]:px-4">
                  <th className="relative w-1/2 min-w-37 pt-4 pb-2.5 font-medium">
                    NAME
                  </th>
                  <th className="relative pt-4 pb-2.5 font-medium whitespace-nowrap">
                    TRACES
                  </th>
                  <th className="relative pt-4 pb-2.5 font-medium whitespace-nowrap">
                    GROUPS
                  </th>
                  <th className="relative pt-4 pb-2.5 font-medium">
                    WORKFLOW DOCUMENTATION
                  </th>
                  <th className="relative w-0 pt-4 pb-2.5 font-medium" />
                </tr>
              </thead>
              <tbody className="text-card-foreground whitespace-nowrap">
                {sortedWorkflows.map(w => {
                  const isFavorite = favWfIds?.includes(w.rowId) || false;
                  return (
                    <WorkflowRow
                      key={w.rowId}
                      workflow={w}
                      isSuperuser={isSuperuser}
                      userId={userId}
                      onFavoriteChange={onFavoriteChange}
                      isFavorite={isFavorite}
                    />
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex h-[86px] w-full items-center justify-center">
              <div className="text-card-foreground text-center text-sm">
                No workflow.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

// export the workflows table query
export const fragments = {
  workflows: gql`
    fragment WorkflowsFragment on Query {
      workflows(filter: { draft: { equalTo: false } }) {
        totalCount
        nodes {
          rowId
          ...WorkflowRowFragment
        }
      }
    }
    ${workflowRowFragments.workflowRow}
  `
};
